import { AxiosHeaders, AxiosResponse } from 'axios';
import ApiService from './api';
import {
  FirebaseIdsRange,
  VrUser,
  CreatedVrUser,
  UpdatedVrUser,
  QueryParams,
  VrUserInternal,
  TagInternal,
  LearningExperience,
  QueryParamsLe,
  StudentCompletionReportResponse,
} from 'shared/types';

export const getVrUsers = async (
  page: number,
  limit: number,
  orderBy: { orderBy?: string },
  search: { search?: string, schoolId?: string, period?: string }
): Promise<AxiosResponse<VrUser[]>> => {
  return await ApiService.get<VrUser[]>('users/students', {
    page,
    limit,
    isStudent: true,
    ...orderBy,
    ...search
  });
};

export const searchVrUsers = async (
  orderBy: string,
  queryParams: QueryParams,
  page?: number,
  limit?: number,
): Promise<AxiosResponse<VrUserInternal[]>> => {
  return await ApiService.get<VrUserInternal[]>('users/report', {
    orderBy,
    ...queryParams,
    isStudent: true,
    page,
    limit,
  });
};

export const searchVrUsersForSelector = async (
  orderBy: string,
  queryParams: QueryParams,
  page?: number,
  limit?: number,
): Promise<AxiosResponse<VrUserInternal[]>> => {
  return await ApiService.get<VrUserInternal[]>('users/select', {
    orderBy,
    ...queryParams,
    isStudent: true,
    page,
    limit,
  });
};

export const getAllLEs = async (
): Promise<AxiosResponse<LearningExperience[]>> => {
  return await ApiService.get<LearningExperience[]>('le/');
};

export const searchVrUsersLECompletion = async (
  orderBy: string,
  queryParams: QueryParams,
  page?: number,
  limit?: number,
): Promise<AxiosResponse<VrUserInternal[]>> => {
  return await ApiService.get<VrUserInternal[]>('users/report/le-completion', {
    orderBy,
    ...queryParams,
    page,
    limit,
  });
};

export const getStudentCompletionReport = async (
  schoolId?: string,
  period?: string,
): Promise<AxiosResponse<StudentCompletionReportResponse>> => {
  return await ApiService.get<StudentCompletionReportResponse>(
    'users/report/student-completion',
    {
      ...(schoolId ? { schoolId } : {}),
      ...(period ? { period } : {}),
    },
  );
};

/** Moves students into one of the current teacher's class periods; null unassigns. */
export const assignClassPeriod = async (
  studentIds: string[],
  period: number | null,
): Promise<AxiosResponse<{ success: boolean; updated: number }>> =>
  await ApiService.put<{ studentIds: string[]; period: number | null }, { success: boolean; updated: number }>(
    'users/class-periods',
    { studentIds, period },
  );

export const searchVrUsersLeTimeInStations = async (
  orderBy: string,
  queryParams: QueryParams,
  page?: number,
  limit?: number,
): Promise<AxiosResponse<VrUserInternal[]>> => {
  return await ApiService.get<VrUserInternal[]>('users/report/le-stations', {
    orderBy,
    ...queryParams,
    page,
    limit,
  });
};

export const getUsersByFirebaseIdRange = async (
  idsRange: FirebaseIdsRange
): Promise<AxiosResponse<VrUser[]>> => {
  return await ApiService.get<VrUser[]>('vr-users', {
    ...idsRange,
    page: 1,
    limit: 1000
  });
};

export const getUsersByFirebaseId = async (id: string): Promise<AxiosResponse<VrUserInternal>> => {
  return await ApiService.get<VrUserInternal>(`vr-users/firebase/${id}`);
};

export const addVrUser = async (vrUser: CreatedVrUser): Promise<AxiosResponse<VrUser>> =>
  await ApiService.post<CreatedVrUser, VrUser>('users/student', vrUser);

export const updateVrUser = async (
  id: string,
  vrUser: UpdatedVrUser
): Promise<AxiosResponse<VrUser>> =>
  await ApiService.patch<UpdatedVrUser, VrUser>(`users/student/${id}`, vrUser);

export const deleteVrUser = async (id: string): Promise<void> =>
  await ApiService.delete<{ users: string[]; isStudent?: boolean; }, void>('users', { users: [id], isStudent: true });

export const downloadAllVrUsersReports = async (
  queryParams: QueryParams
): Promise<void> => {
  try {
    const response = await ApiService.get<Blob>(`vr-users/export`, { ...queryParams });

    const contentDisposition = response.headers['content-disposition'];

    const matches = /filename="(.+)"/.exec(contentDisposition);
    const filename =
      matches && matches[1] ? matches[1] : `superadmin_vr-users_reports_${Date.now()}.csv`;

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading the CSV file:', error);
    throw error;
  }
};

export const assignAdminTagToVrUser = async (userId: string): Promise<void> => {
  try {
    await ApiService.post(`users/tag/`, { userId })
  } catch (e) {
    console.error('Error assign admin tag to user', e)
    throw e;
  }
}

export const checkTagAssignVrUser = async (
  vrUserId: string,
  adminId: string
): Promise<boolean> => {
  try {
    const res: AxiosResponse<TagInternal> = await ApiService.get(`users/tag/check?adminId=${adminId}&userId=${vrUserId}`);
    return !!res?.data;
  } catch (e) {
    console.error('Error assign admin tag to Vr user', e);
    throw e;
  }
};

export const downloadAllUsersReports = async (
  queryParams: QueryParamsLe
): Promise<void> => {
  try {
    const response = await ApiService.get<Blob>(`users/export`, { ...queryParams });

    const contentDisposition = response.headers['content-disposition'];

    const matches = /filename="(.+)"/.exec(contentDisposition);
    const filename =
      matches && matches[1] ? matches[1] : `user_reports_${Date.now()}.csv`;

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading the CSV file:', error);
    throw error;
  }
};

export const downloadAllLeCompletionReports = async (
  queryParams: QueryParamsLe
): Promise<void> => {
  try {
    const response = await ApiService.post<QueryParamsLe, Blob>(
      'users/export-completion',
      queryParams,
      {
        headers: new AxiosHeaders(),
        responseType: 'blob'
      } 
    );
    const contentDisposition = response.headers['content-disposition'];

    const matches = /filename="(.+)"/.exec(contentDisposition);
    const filename =
      matches && matches[1] ? matches[1] : `le_completion_user_reports_${Date.now()}.csv`;

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading the CSV file:', error);
    throw error;
  }
};

export const downloadAllLeTimeInStationsReports = async (
  queryParams: QueryParamsLe
): Promise<void> => {
  try {
    const response = await ApiService.get<Blob>(`users/export-time-stations`, { ...queryParams });

    const contentDisposition = response.headers['content-disposition'];

    const matches = /filename="(.+)"/.exec(contentDisposition);
    const filename =
      matches && matches[1] ? matches[1] : `le_time_in_stations_user_reports_${Date.now()}.csv`;

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading the CSV file:', error);
    throw error;
  }
};
